import { useQueryClient } from "@tanstack/react-query";
import { FaPlus } from "react-icons/fa";
import { Button, FormGroup, Label } from "reactstrap";
import Select from 'react-select';
import { EmployeeConfigMasterFormModal, EmployeeConfigMasterTable } from "./components";
import axios, { AxiosError } from "axios";
import { useCompanyMaster } from "queries";
import { useMemo, useState } from "react";
import { CompanyMaster } from "models/companymaster/CompanyMaster";
import { useAddEmployeeConfigMaster, useDeleteEmployeeConfigMaster, useEmployeeConfigMaster } from "./queries";
import { useBoolean } from "usehooks-ts";
import { EmployeeConfigMaster } from "models/config/EmployeeConfigMaster";
import { extractAccessToken } from "helpers/jwt";
import Swal from "sweetalert2";
import { useUserInformation } from "hooks";

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const EmployeeRoles = () => {
    const queryClient = useQueryClient();

	const { companyOptions } = useUserInformation()
    const { data: companyMaster } = useCompanyMaster();

    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();

    const { data: employeeConfigMaster, isFetching: isFetchingEmployeeConfigMaster } = useEmployeeConfigMaster();
    const filteredEmployeeConfigMaster = useMemo(
        () =>
            employeeConfigMaster?.filter(employeeConfig =>
                selectedCompany && selectedCompany.sid !== 'all'
                    ? employeeConfig.sid === selectedCompany.sid
                    : true
            ),
        [selectedCompany, employeeConfigMaster]
    );
    
    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addEmployeeConfigMaster } = useAddEmployeeConfigMaster();

    const { mutate: deleteEmployeeConfigMaster } = useDeleteEmployeeConfigMaster();

    const handleSubmitAddEmployeeConfigMaster = (employeeConfigMasters: EmployeeConfigMaster) => {
        const request = {
            ...employeeConfigMasters,
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        }

        addEmployeeConfigMaster(request, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                toggleIsOpenCreateModal();

                queryClient.invalidateQueries({
                    queryKey: ['employee-config-master']
                });
            }
        });
    };

    const handleDeleteEmployeeConfigMaster = async (employeeConfigMaster: EmployeeConfigMaster) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${employeeConfigMaster?.id}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }
        
        deleteEmployeeConfigMaster(employeeConfigMaster, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['employee-config-master']
                });
            }
        });
    };

    return (
        <div className="master-data-content">
            <div className="tw-flex tw-justify-between">
                <div className="trapezoid-head tw-w-[200px]">
                    <span>Employee Roles</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="tw-flex tw-items-center tw-gap-1 tw-self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>

            <div className="mat-box">
                <div className="tw-flex tw-justify-between">
                    <div className="trapezoid-head tw-w-fit">
                        <span>Search</span>
                    </div>
                </div>
                <div className="mat-box tw-flex tw-flex-col">
                    <FormGroup className="w-full md:w-1/3">
                        <Label for="sid">Company</Label>
                        <Select
                            options={companyOptions}
                            value={selectedCompany}
                            getOptionLabel={(company: CompanyMaster) =>
                                company.company_name
                            }
                            getOptionValue={(company: CompanyMaster) =>
                                company.sid
                            }
                            onChange={setSelectedCompany}
                        />
                    </FormGroup>
                </div>

                <EmployeeConfigMasterTable
                    isPending={isFetchingEmployeeConfigMaster}
                    employeeConfigs={filteredEmployeeConfigMaster}
                    onDelete={handleDeleteEmployeeConfigMaster}
                />

                {/* Create modal */}
                <EmployeeConfigMasterFormModal
                    title="Create Employee Config"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddEmployeeConfigMaster}
                />
            </div>
        </div>
    )
};